import type { ConfirmRfqSourceInviteRequest, CreateRfqSourceInviteRequest } from '@console/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchCsrfToken } from '../auth/api.js'
import {
  confirmRfqSourceInvite,
  createRfqSourceInvite,
  fetchRfqSourceInvites,
  revokeRfqSourceInvite,
} from './api.js'

const QUERY_KEY = (tenantId: string) => ['rfq-source-invites', tenantId] as const

/**
 * No `staleTime` override — this list is small, opened deliberately (not
 * polled), and every mutation below invalidates it directly rather than
 * relying on a background refetch to catch up.
 */
export function useRfqSourceInvites(tenantId: string) {
  return useQuery({
    queryKey: QUERY_KEY(tenantId),
    queryFn: () => fetchRfqSourceInvites(tenantId),
  })
}

export function useCreateRfqSourceInvite(tenantId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: CreateRfqSourceInviteRequest) => {
      const csrfToken = await fetchCsrfToken()
      return createRfqSourceInvite(tenantId, body, csrfToken)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY(tenantId) })
    },
  })
}

export function useConfirmRfqSourceInvite(tenantId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      inviteId,
      ...body
    }: ConfirmRfqSourceInviteRequest & { inviteId: string }) => {
      const csrfToken = await fetchCsrfToken()
      return confirmRfqSourceInvite(inviteId, body, csrfToken)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY(tenantId) })
    },
  })
}

export function useRevokeRfqSourceInvite(tenantId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (inviteId: string) => {
      const csrfToken = await fetchCsrfToken()
      return revokeRfqSourceInvite(inviteId, csrfToken)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY(tenantId) })
    },
  })
}
